import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles, Send, MapPin, Wifi, Car, Sun } from 'lucide-react';

interface DemoFloatingAssistantProps {
  darkMode: boolean;
  /** Show pulse ring on the floating button (e.g. as visual cue) */
  pulse?: boolean;
  /** Called when the assistant panel is opened */
  onOpen?: () => void;
}

interface DemoMessage {
  role: 'user' | 'assistant';
  content: string;
  isTyping?: boolean;
}

const SUGGESTIONS = [
  { icon: Wifi, key: 'wifi', textHr: 'Koja je WiFi lozinka?', textEn: 'WiFi password?' },
  { icon: Car, key: 'parking', textHr: 'Gdje je parking?', textEn: 'Where is parking?' },
  { icon: MapPin, key: 'beach', textHr: 'Najbliža plaža?', textEn: 'Nearest beach?' },
  { icon: Sun, key: 'checkout', textHr: 'Kad je odjava?', textEn: 'Check-out time?' },
];

const SCRIPTED_REPLIES: Record<string, { hr: string; en: string }> = {
  wifi: {
    hr: '📶 WiFi mreža: **VillaEma_5G**\n🔑 Lozinka: **Makarska2026!**\n\nMožete kopirati podatke jednim klikom u Home tabu!',
    en: '📶 Network: **VillaEma_5G**\n🔑 Password: **Makarska2026!**\n\nYou can copy these with one tap in the Home tab!',
  },
  parking: {
    hr: '🅿️ Privatni parking je **50m iznad kuće**.\n\nSlijedite cestu uzbrdo i skrenite desno na makadamski put. Imamo 4 mjesta. Detaljne upute s GPS-om su u tabu Smještaj.',
    en: '🅿️ Private parking is **50m above the house**.\n\nFollow the road uphill and turn right onto the gravel path. We have 4 spots. Detailed GPS instructions are in the Unit tab.',
  },
  beach: {
    hr: '🏖️ Plaža je **doslovno ispred kuće**! Samo siđete stepenicama.\n\nŠljunčana je s plitkim morem - savršeno za djecu. Pogledajte tab Istraži za više preporuka.',
    en: '🏖️ The beach is **right in front of the house**! Just walk down the steps.\n\nPebble beach with shallow waters - perfect for kids. Check the Explore tab for more recommendations.',
  },
  checkout: {
    hr: '⏰ Odjava je do **10:00**.\n\nMolimo ostavite ključ u lockboxu (kod 4523). Plahte i ručnike ostavite u kupaonici. Hvala!',
    en: '⏰ Check-out is by **10:00**.\n\nPlease leave the key in the lockbox (code 4523). Leave sheets and towels in the bathroom. Thank you!',
  },
};

export function DemoFloatingAssistant({
  darkMode,
  pulse = false,
  onOpen,
}: DemoFloatingAssistantProps) {
  const { i18n } = useTranslation();
  const lang = (i18n.language?.split('-')[0] || 'hr') as 'hr' | 'en';
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<DemoMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSuggestion = (suggestion: typeof SUGGESTIONS[number]) => {
    const userText = lang === 'hr' ? suggestion.textHr : suggestion.textEn;
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: userText },
      { role: 'assistant', content: '', isTyping: true },
    ]);

    setTimeout(() => {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: SCRIPTED_REPLIES[suggestion.key][lang],
        };
        return next;
      });
    }, 900);
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => {
            setIsOpen(true);
            onOpen?.();
          }}
          className="absolute bottom-24 right-4 z-40 group"
          aria-label="AI Assistant"
        >
          <div className="relative">
            {/* Pulse ring (when triggered by demo cycler) */}
            {pulse && (
              <>
                <span className="absolute inset-0 rounded-full bg-cyan-400 opacity-40 animate-ping" />
                <span
                  className="absolute inset-0 rounded-full bg-blue-400 opacity-30 animate-ping"
                  style={{ animationDelay: '500ms' }}
                />
              </>
            )}

            <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 shadow-2xl shadow-blue-500/50 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Sparkles className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>

            {/* Online dot */}
            <span
              className={`absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-green-500 ring-2 ${
                darkMode ? 'ring-slate-900' : 'ring-white'
              }`}
            />
          </div>
        </button>
      )}

      {/* Assistant Panel */}
      {isOpen && (
        <div className="absolute inset-0 z-[55] flex flex-col">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div
            className={`absolute bottom-0 left-0 right-0 h-[75%] rounded-t-3xl flex flex-col shadow-2xl animate-in slide-in-from-bottom duration-300 ${
              darkMode ? 'bg-slate-900' : 'bg-white'
            }`}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div
                className={`w-10 h-1 rounded-full ${
                  darkMode ? 'bg-slate-600' : 'bg-gray-300'
                }`}
              />
            </div>

            {/* Header */}
            <div
              className={`flex items-center gap-3 px-4 py-3 border-b ${
                darkMode ? 'border-slate-800' : 'border-gray-100'
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  className={`font-bold text-sm ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {lang === 'hr' ? 'AI Asistent' : 'AI Assistant'}
                </h3>
                <p
                  className={`text-[11px] ${
                    darkMode ? 'text-cyan-400' : 'text-cyan-600'
                  }`}
                >
                  {lang === 'hr' ? 'Dostupan 24/7' : 'Available 24/7'}
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className={`p-2 rounded-full ${
                  darkMode
                    ? 'hover:bg-slate-800 text-slate-300'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div
              className={`flex-1 overflow-y-auto px-4 py-4 space-y-3 ${
                darkMode
                  ? 'bg-gradient-to-b from-slate-900 to-slate-950'
                  : 'bg-gradient-to-b from-gray-50 to-white'
              }`}
            >
              {/* Welcome card */}
              {messages.length === 0 && (
                <div
                  className={`p-4 rounded-2xl text-center ${
                    darkMode
                      ? 'bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20'
                      : 'bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-100'
                  }`}
                >
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-md">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <p
                    className={`text-sm font-semibold ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {lang === 'hr'
                      ? 'Bok! Kako mogu pomoći?'
                      : 'Hi! How can I help?'}
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      darkMode ? 'text-slate-400' : 'text-gray-500'
                    }`}
                  >
                    {lang === 'hr'
                      ? 'Postavite pitanje ili odaberite ispod'
                      : 'Ask a question or choose below'}
                  </p>
                </div>
              )}

              {/* Messages */}
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-[20px] ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-md shadow-md shadow-blue-500/20'
                        : darkMode
                        ? 'bg-slate-800 text-white rounded-bl-md'
                        : 'bg-white border border-gray-100 text-gray-900 rounded-bl-md shadow-sm'
                    }`}
                  >
                    {msg.isTyping ? (
                      <div className="flex gap-1 py-1">
                        <span
                          className={`w-2 h-2 rounded-full animate-bounce ${
                            darkMode ? 'bg-slate-400' : 'bg-gray-400'
                          }`}
                          style={{ animationDelay: '0ms' }}
                        />
                        <span
                          className={`w-2 h-2 rounded-full animate-bounce ${
                            darkMode ? 'bg-slate-400' : 'bg-gray-400'
                          }`}
                          style={{ animationDelay: '150ms' }}
                        />
                        <span
                          className={`w-2 h-2 rounded-full animate-bounce ${
                            darkMode ? 'bg-slate-400' : 'bg-gray-400'
                          }`}
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    ) : (
                      <p
                        className="text-sm leading-relaxed whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{
                          __html: msg.content.replace(
                            /\*\*(.+?)\*\*/g,
                            '<strong>$1</strong>'
                          ),
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick suggestions */}
            <div
              className={`px-3 pt-2 pb-3 border-t ${
                darkMode ? 'border-slate-800 bg-slate-900' : 'border-gray-100 bg-white'
              }`}
            >
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {SUGGESTIONS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.key}
                      onClick={() => handleSuggestion(s)}
                      className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-all hover:scale-105 ${
                        darkMode
                          ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 text-cyan-500" />
                      {lang === 'hr' ? s.textHr : s.textEn}
                    </button>
                  );
                })}
              </div>

              {/* Input (visual only) */}
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-2xl ${
                  darkMode ? 'bg-slate-800' : 'bg-gray-100'
                }`}
              >
                <input
                  type="text"
                  placeholder={
                    lang === 'hr' ? 'Postavite pitanje...' : 'Ask a question...'
                  }
                  className={`flex-1 bg-transparent text-sm outline-none ${
                    darkMode
                      ? 'text-white placeholder-slate-500'
                      : 'text-gray-900 placeholder-gray-400'
                  }`}
                />
                <button
                  className={`p-2 rounded-full ${
                    darkMode
                      ? 'bg-slate-700 text-slate-400'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DemoFloatingAssistant;
